import {
  Heading, HStack, Text, VStack,
} from '@gluestack-ui/themed';

import { LabNavLink } from '@/visual-lab/LabNavLink';

type VisualLabIndexProps = {
  onNavigate: (path: string) => void;
};

type ChapterCardProps = {
  title: string;
  description: string;
  href: string;
  onNavigate: (path: string) => void;
};

function ChapterCard({
  title, description, href, onNavigate,
}: ChapterCardProps) {
  return (
    <LabNavLink
      accessible
      accessibilityRole="link"
      accessibilityLabel={title}
      href={href}
      onPress={() => onNavigate(href)}
      borderWidth={1}
      borderColor="$borderLight"
      borderRadius="$xl"
      bg="$bgCard"
      p="$4"
      flex={1}
      minWidth={240}
      maxWidth={360}
    >
      <VStack space="sm">
        <Heading size="md">{title}</Heading>
        <Text size="sm" color="$textSubtle">{description}</Text>
      </VStack>
    </LabNavLink>
  );
}

/**
 * Visual Lab index — chapter cards for each commissioning playground.
 */
export function VisualLabIndex({ onNavigate }: VisualLabIndexProps) {
  return (
    <VStack space="lg" px="$5" py="$6" maxWidth={960} alignSelf="center" width="100%">
      <VStack space="xs">
        <Heading size="xl">Visual Lab</Heading>
        <Text size="sm" color="$textSubtle">
          Local visual commissioning playground. Open a chapter to tune UI in isolation.
        </Text>
      </VStack>

      <HStack space="md" flexWrap="wrap">
        <ChapterCard
          title="Orb Lab"
          description="Tune the unified AI presence orb across states, sizes, and backdrops."
          href="/visual-lab/orb"
          onNavigate={onNavigate}
        />
        <ChapterCard
          title="Toast Lab"
          description="Commission portal toast layout on a contrast canvas — short, overflow, and unbreakable copy."
          href="/visual-lab/toast"
          onNavigate={onNavigate}
        />
        <ChapterCard
          title="Eval Card Lab"
          description="Mixed-level evaluation dimensions with gate marks and optional level lines — pick before the student card ships."
          href="/visual-lab/eval-card"
          onNavigate={onNavigate}
        />
        <ChapterCard
          title="Oral Exam"
          description="Production supervisor, student, and pre-flight review screens with e2e stand-in faces — switch actor, stage, or pre-flight state in the header."
          href="/visual-lab/oral-exam"
          onNavigate={onNavigate}
        />
        <ChapterCard
          title="Oral exam task"
          description="The student oral-exam column and desktop task page in every booking state: locked, can book, booked, lobby open, missed, cancelled, taken, failed."
          href="/visual-lab/oral-exam-task"
          onNavigate={onNavigate}
        />
        <ChapterCard
          title="Grade report"
          description="Production GradeReport with representative grading scenarios — switch fixtures without login."
          href="/visual-lab/grade-report"
          onNavigate={onNavigate}
        />
        <ChapterCard
          title="Upgrade offer"
          description="The student module-upgrade ribbon and dialog with fixture offers — check the dialog fits a short window."
          href="/visual-lab/upgrade-offer"
          onNavigate={onNavigate}
        />
        <ChapterCard
          title="Analytics"
          description="Production AI Tutor admin dashboard — Attempts figure and filter bar via contract fixture (?state=ready|too-few|empty|error)."
          href="/visual-lab/analytics?state=too-few"
          onNavigate={onNavigate}
        />
      </HStack>
    </VStack>
  );
}
